/**
 * Composable for checking user permissions (DB-based)
 * Usage:
 *   const { user, hasModule, isAdmin, isSuperAdmin, loading } = usePermissions();
 *   if (hasModule('cloud')) { ... }
 */

import { ref, computed } from 'vue';
import { apiGet, ApiError } from '@/composables/useApi.js';

const currentUser = ref(null);
const loading = ref(false);
const error = ref(null);

export function usePermissions() {
  /**
   * Load current user from API
   */
  async function loadUser() {
    try {
      loading.value = true;
      error.value = null;

      const data = await apiGet('/api/users/me');
      currentUser.value = data.user;
      return data.user;

    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Not authenticated — expected when user is not logged in
        currentUser.value = null;
        return null;
      }
      console.error('Error loading user:', err);
      error.value = err.message;
      currentUser.value = null;
      return null;

    } finally {
      loading.value = false;
    }
  }

  /**
   * Check if user has access to a specific module
   * Admin/superadmin bypass all module checks
   * @param {string} mod - Module key (chat, cloud, analyzy, vypocty, freshdesk, mapa, projekty, admin)
   * @returns {boolean}
   */
  function hasModule(mod) {
    if (!currentUser.value) return false;
    if (['admin', 'superadmin'].includes(currentUser.value.role)) return true;
    const perms = currentUser.value.permissions;
    if (!perms) return false;
    return perms[mod] === true;
  }

  const isSuperAdmin = computed(() => {
    return currentUser.value?.role === 'superadmin';
  });

  const isAdmin = computed(() => {
    return ['admin', 'superadmin'].includes(currentUser.value?.role);
  });

  const isAuthenticated = computed(() => {
    return currentUser.value !== null;
  });

  const permissions = computed(() => {
    return currentUser.value?.permissions || {};
  });

  return {
    user: currentUser,
    loading,
    error,
    loadUser,
    hasModule,
    isAdmin,
    isSuperAdmin,
    isAuthenticated,
    permissions
  };
}
